import { Mail, MapPin, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import ContactForm from "@/components/ContactForm";
import { Container, Reveal } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { COMPANY, FAQS } from "@/data/content";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact"
        description="Contact Innervation Energy Group to discuss commercial electricity, natural gas procurement, or Virtual Power Plant Network hosting opportunities."
        path="/contact"
      />
      <PageHero
        overline="Contact"
        titleLines={["Let's talk", "energy strategy."]}
        subtitle="Tell us about your facilities and what you'd like help with. A commercial energy advisor will follow up shortly."
        crumbs={[{ label: "Contact", path: "/contact" }]}
      />

      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            {/* Info */}
            <div className="lg:col-span-5">
              <p className="overline text-electric">Get in touch</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                We're here to help.
              </h2>
              <p className="mt-5 leading-relaxed text-slatey">
                Whether you're renewing a contract, entering a new market, or hosting
                battery storage in our Virtual Power Plant Network, reach out and we'll point you in
                the right direction.
              </p>

              <div className="mt-10 space-y-5">
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-start gap-4"
                  data-testid="contact-email-link"
                >
                  <span className="flex h-11 w-11 items-center justify-center border border-line text-electric">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                      Email
                    </span>
                    <span className="mt-1 block font-medium text-navy">
                      {COMPANY.email}
                    </span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-line text-electric">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                      Coverage
                    </span>
                    <span className="mt-1 block font-medium text-navy">
                      Deregulated U.S. energy markets
                    </span>
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-line text-electric">
                    <Clock className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                      Response
                    </span>
                    <span className="mt-1 block font-medium text-navy">
                      Within one business day
                    </span>
                  </span>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="font-heading text-lg font-medium text-navy">
                  Frequently asked
                </h3>
                <Accordion type="single" collapsible className="mt-4">
                  {FAQS.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                      <AccordionTrigger className="text-left text-navy hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slatey">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <div className="border border-line bg-ice p-8 md:p-10">
                  <ContactForm />
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
